<template>
  <v-navigation-drawer
    v-model="isShown"
    data-cy="adminNavMenu"
    mobile-breakpoint="md"
    location="left"
    width="300"
    :class="['mini-nav business', { 'mini-nav--is-locked': isLocked }]"
  >
    <nav-locked-overlay v-if="isLocked" />
    <!-- Top navigation items -->
    <div class="d-flex flex-column pt-9 | mini-nav__flex">
      <nuxt-link :to="{ name: 'business-profile' }" class="text-no-decoration pl-5">
        <logo data-cy="indexLogo" admin-logo-variant="light" />
      </nuxt-link>
      <v-list class="py-0 pt-10 mini-nav__top">
        <template v-for="(item, index) in navMenuStructure.topStructure" :key="item.id">
          <div v-if="item.isEnabled" :class="{ 'pb-3': index !== navMenuStructure.topStructure.length - 1 }">
            <v-list-item
              :prepend-icon="item.icon"
              :to="item.route"
              :data-cy="item.id"
              :class="{ 'mini-nav__custom-extras': item.id === 'extras' }"
            >
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item>
            <template v-if="item.children">
              <template v-for="child in item.children">
                <v-list-item
                  v-if="item.isEnabled && child.isEnabled"
                  :key="child.id"
                  prepend-icon=" "
                  exact
                  :to="child.route"
                >
                  <span class="mini-nav__border" />
                  <v-list-item-action />

                  <v-list-item-title>
                    {{ child.title }}
                  </v-list-item-title>
                </v-list-item>
              </template>
            </template>
          </div>
        </template>
        <div v-if="!isIndividualPlan" class="settings-nav mt-5" data-cy="settings">
          <v-divider />
          <span class="settings-nav__heading">{{ $t('business.navigation.settings') }}</span>
          <template v-for="item in navMenuStructure.settingsStructure" :key="item.id">
            <v-list-item v-if="item.isEnabled" :prepend-icon="item.icon" :to="item.route" exact :data-cy="item.id">
              <span class="mini-nav__border" />
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </v-list-item>
          </template>
        </div>
      </v-list>
      <!-- Bottom navigation items -->
      <v-list class="mt-auto py-0 pt-4 | business-nav-bottom">
        <template v-for="item in navMenuStructure.bottomStructure">
          <div v-if="item.isEnabled" :key="item.id">
            <v-divider />
            <!-- Item with no children -->
            <template v-if="!item.children?.length">
              <v-list-item :key="item.id" :prepend-icon="item.icon" :to="item.route" :data-cy="item.id">
                <v-list-item-title>
                  {{ item.title }}
                </v-list-item-title>
              </v-list-item>
            </template>
            <!-- Item with expandable list of child items -->
            <template v-else-if="isSmallScreen">
              <v-list-group :key="item.id" :data-cy="item.id">
                <template #activator="{ props }">
                  <v-list-item v-bind="props" :prepend-icon="item.icon" :class="{ 'business-profile': item.avatar }">
                    <!--     prepend-avatar works only for image url, need to add it manually-->
                    <div class="business-profile-avatar">
                      <v-avatar v-if="item.avatar" color="primary" size="40px" class="text-white mr-3">{{
                        item.avatar
                      }}</v-avatar>
                      <div>
                        <v-list-item-title>{{ item.title }}</v-list-item-title>
                        <v-list-item-subtitle
                          v-if="item.subtitle"
                          class="text-truncate | mini-nav__business-profile__subtitle"
                        >
                          {{ item.subtitle }}
                        </v-list-item-subtitle>
                      </div>
                    </div>
                  </v-list-item>
                </template>
                <template v-for="child in item.children">
                  <v-list-item
                    v-if="child.isEnabled"
                    :key="child.id"
                    :prepend-icon="child.icon"
                    :to="child.route"
                    :href="child.link"
                    :target="child.link ? '_blank' : undefined"
                    :data-cy="child.id"
                  >
                    <v-list-item-title>
                      {{ child.title }}
                    </v-list-item-title>
                    <v-list-item-subtitle v-if="child.subtitle">
                      {{ child.subtitle }}
                    </v-list-item-subtitle>
                  </v-list-item>
                </template>
              </v-list-group>
            </template>
            <!-- Item with flyout menu listing child items -->
            <template v-else-if="!isSmallScreen">
              <div :key="item.id">
                <v-list-item
                  :id="item.id"
                  class="business-nav-bottom-flyout"
                  :prepend-icon="item.icon"
                  :class="{ 'business-profile business-profile-padding': item.avatar }"
                  :data-cy="item.id"
                >
                  <!--  prepend-avatar works only for image url, need to add it manually-->
                  <div class="business-profile-avatar">
                    <v-avatar v-if="item.avatar" color="primary" size="40px" class="text-white mr-3">{{
                      item.avatar
                    }}</v-avatar>
                    <div>
                      <v-list-item-title>{{ item.title }}</v-list-item-title>
                      <v-list-item-subtitle
                        v-if="item.subtitle"
                        class="text-truncate | mini-nav__business-profile__subtitle"
                      >
                        {{ item.subtitle }}
                      </v-list-item-subtitle>
                    </div>
                  </div>

                  <template #append>
                    <v-icon color="#B3D4F7" size="1rem">custom:arrow_right</v-icon>
                  </template>
                </v-list-item>
                <v-menu
                  class="business-nav-menu"
                  location="end"
                  open-on-hover
                  :offset="[0, Number(item.menuOffset ?? '0')]"
                  :activator="`#${item.id}`"
                  min-width="200px"
                  content-class="nav-overlay-menu"
                >
                  <v-card>
                    <v-list nav class="pa-1">
                      <template v-for="child in item.children">
                        <v-list-item
                          v-if="child.isEnabled"
                          :key="child.id"
                          :prepend-icon="child.icon"
                          :to="child.route"
                          :href="child.link"
                          :target="child.link ? '_blank' : undefined"
                          exact
                          :data-cy="child.id"
                        >
                          <v-list-item-title>
                            {{ child.title }}
                          </v-list-item-title>
                          <v-list-item-subtitle v-if="child.subtitle">
                            {{ child.subtitle }}
                          </v-list-item-subtitle>
                        </v-list-item>
                      </template>
                    </v-list>
                  </v-card>
                </v-menu>
              </div>
            </template>
          </div>
        </template>
      </v-list>
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import NavLockedOverlay from '@/components/business/NavLockedOverlay.vue'
import Logo from '@/components/Logo.vue'
import type { RouteLocationRaw } from 'vue-router'

interface NavItem {
  id: string
  title: string
  subtitle?: string
  avatar?: string
  menuOffset?: string
  link?: string
  route?: RouteLocationRaw
  isEnabled?: boolean
  icon?: string
  children?: NavItem[]
}

export default defineComponent({
  components: {
    Logo,
    NavLockedOverlay,
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    isLocked: Boolean,
  },
  emits: ['update:modelValue'],
  setup() {
    const { $vuetify } = useNuxtApp()
    const { t } = useI18n()
    const businessStore = useBusinessStore()
    const { isIndividualPlan, isScalePlan, isTrialPlan, isProPlan } = useUserPlanInfo()
    const systemStore = useSystemStore()
    const { isManageGroupsFlag } = useNavigationFlags()

    const supportContact = computed(() => businessStore.settings.supportContact)

    const isSmallScreen = computed(() => $vuetify.display.smAndDown.value)

    const seals = computed(() => businessStore.seals)
    const fetchSeals = async () => {
      await businessStore.getSeals()
    }

    const platform = computed(() => systemStore.platform || 'CH')

    const navMenuStructure = computed(() => {
      const topStructure: NavItem[] = [
        {
          id: 'dashboard',
          title: 'Dashboard',
          icon: 'custom:f_dashboard',
          route: { name: 'business-dashboard' },
          isEnabled: !isIndividualPlan.value,
        },
        {
          id: 'team',
          title: t('business.navigation.team'),
          icon: 'custom:multiple',
          route: { name: 'business-members' },
          isEnabled: !isIndividualPlan.value,
          children: [
            {
              id: 'members',
              title: t('business.navigation.members'),
              route: { name: 'business-members' },
              isEnabled: true,
            },
            {
              id: 'domains',
              title: t('business.members.navigation.domains'),
              route: { name: 'business-members-domains' },
              isEnabled: isScalePlan.value,
            },
            {
              id: 'groups',
              title: t('business.navigation.groups'),
              route: { name: 'business-members-groups' },
              isEnabled: isManageGroupsFlag.value,
            },
          ],
        },
        {
          id: 'plan',
          title: t('business.navigation.plan_and_billing'),
          icon: 'custom:wallet',
          route: { name: 'business-billing-plan' },
          isEnabled: true,
        },
        {
          id: 'activity',
          title: t('business.navigation.activity'),
          icon: 'custom:lightning_big',
          route: { name: 'business-activity' },
          isEnabled: !isIndividualPlan.value,
        },
        {
          id: 'seals',
          title: t('business.navigation.seal'),
          icon: 'custom:seal',
          route: { name: 'business-seals' },
          isEnabled: Boolean(!isIndividualPlan.value && seals.value.length),
        },
        {
          id: 'api',
          title: t('business.navigation.developers'),
          icon: 'custom:developers',
          route: { name: 'business-developers-api' },
          isEnabled: isScalePlan.value || isProPlan.value || isTrialPlan.value,
        },
        {
          id: 'extras',
          title: t('business.navigation.extras'),
          icon: 'custom:integration',
          route: { name: 'business-extras' },
          isEnabled: isProPlan.value || isTrialPlan.value,
        },
      ]

      const settingsStructure: NavItem[] = [
        {
          id: 'emails',
          title: t('business.navigation.emails'),
          icon: 'custom:email',
          route: { name: 'business-emails' },
          isEnabled: true,
        },
        {
          id: 'documents',
          title: t('business.navigation.documents'),
          icon: 'custom:document_outline',
          route: { name: 'business-document' },
          isEnabled: isScalePlan.value || isProPlan.value || isTrialPlan.value,
        },
      ]

      const bottomStructure: NavItem[] = [
        {
          id: 'help',
          title: t('global.navigation.help_center'),
          icon: 'custom:c_question',
          route: { name: 'business-document' },
          isEnabled: true,
          children: [
            {
              id: 'admin_guide',
              title: t('global.navigation.admin_guide.label'),
              icon: 'custom:admin_guide',
              link: t('global.navigation.admin_guide.link'),
              isEnabled: true,
            },
            {
              id: 'help_center',
              title: t('global.navigation.helpcenter.label'),
              icon: 'custom:help_center',
              link: t('global.navigation.helpcenter.link'),
              isEnabled: true,
            },
            {
              id: 'video_tutorials',
              title: t('global.navigation.tutorials.label'),
              icon: 'custom:video_tutorials',
              link: t('global.navigation.tutorials.link'),
              isEnabled: true,
            },
            {
              id: 'contact_email',
              title: t('global.navigation.contact.label'),
              icon: 'custom:contact_email',
              link:
                // if business has internal level support - return always eng contact form
                businessStore.settings.supportContact?.email
                  ? t('global.navigation.contact.link_always_en')
                  : platform.value === 'EU'
                    ? t('global.navigation.contact.link_form_de')
                    : t('global.navigation.contact.link'),
              isEnabled: true,
            },
            {
              id: 'contact_phone',
              title: t('global.navigation.call.label'),
              subtitle: supportContact.value.adminPhone ?? '',
              icon: 'custom:support_hotline',
              link:
                systemStore.platform === 'EU'
                  ? t('global.navigation.call.call_support_form_de')
                  : t('global.navigation.call.call_support_form'),
              isEnabled: Boolean(supportContact.value.adminPhone),
            },
          ],
          // TODO: Compute programmatically
          menuOffset: '104',
        },
        {
          id: 'my_skribble',
          title: t('global.navigation.back_to_skribble'),
          icon: 'custom:go_to_skribble',
          route: { path: '/' },
          isEnabled: true,
        },
        {
          id: 'business_profile',
          title: t('business.navigation.business_profile'),
          subtitle: businessStore.name,
          avatar: t('business.navigation.business_profile_abbr'),
          route: { path: '/' },
          isEnabled: true,
          children: [
            {
              id: 'profile',
              title: t('business.navigation.business_profile'),
              icon: 'custom:business_profile',
              route: { name: 'business-profile' },
              isEnabled: true,
            },
            {
              id: 'aes_4_biz',
              title: t('business.profile.navigation.aes'),
              icon: 'custom:business_aes',
              route: { name: 'business-profile-aes-for-business' },
              isEnabled: isScalePlan.value,
            },
            {
              id: 'logout',
              title: t('global.navigation.logout'),
              icon: 'custom:leave',
              route: { name: 'logout' },
              isEnabled: true,
            },
          ],
          menuOffset: '56',
        },
      ]

      return { topStructure, settingsStructure, bottomStructure }
    })

    onBeforeMount(() => {
      void fetchSeals()
    })

    return {
      businessStore,
      seals,
      isSmallScreen,
      isIndividualPlan,
      navMenuStructure,
    }
  },
  computed: {
    isShown: {
      get(): boolean {
        return this.modelValue
      },
      set(newValue: boolean) {
        this.$emit('update:modelValue', newValue)
      },
    },
  },
  watch: {
    isSmallScreen(isMobile: boolean) {
      if (isMobile) this.isShown = false
    },
  },
  methods: {
    upgrade() {
      return navigateTo({ name: 'business-upgrade' })
    },
  },
})
</script>

<style lang="sass" scoped>
.mini-nav
  &--is-locked
    pointer-events: none
    a > .logo
      visibility: hidden
  .v-divider
    border-color: $c-grey-dark
</style>

<style lang="sass">
// TODO: Clean up and remove unused styles, this is a copied-over mess
.nav-overlay-menu
  padding-left: 10px

.mini-nav.business
  padding: 0
  &.v-navigation-drawer
    background-color: $c-mininav-bg

  .v-avatar
    font-size: 1rem
    color: $c-white

  .v-divider
    border-color: rgba($c-mininav-color, 0.2)
    opacity: 1

  .v-list-item
    padding: 0 0 0 20px
    min-height: 32px
    margin: 0
    &.business-profile
      padding-left: 12px
    &:before
      +mini-nav-item-hover
      background: $c-primary
    &.is-large:not(&--active):before
      top: 9px
      width: 46px
      height: 46px
      background: $c-mininav-primary-bg
    &:hover
      background: rgba($c-primary, .12)
      color: $c-white
      &:before
        opacity: 0.1
    &__prepend
      width: 22px
      margin: 0 14px 0 0 !important
      > .v-icon
        opacity: 1
    &-title
      padding: 5px 0
      font-weight: 700
    .v-list-item__overlay
      background-color: transparent
    &--active
      color: $c-white !important
      .mini-nav__border
        &:before
          top: 3px
          bottom: 3px
          left: 0
          transform: none
          width: 8px
          opacity: 1
          border-radius: 0 4px 4px 0
          position: absolute
          color: $c-mininav-primary-bg
          background-color: currentColor
          content: ""
          +mini-nav-item-hover
      &:hover
        color: $c-white !important
        .v-list-item-title
          color: $c-white !important
    &-title
      font-size: 0.875rem
      line-height: 1.2
      +mini-nav-item-hover
    &:not(.v-list-item--active)
      color: $c-mininav-color !important
    &:hover
      color: $c-white !important
      .v-list-item-title
        color: $c-white !important
    .v-icon, svg
      color: inherit
      width: 22px
      height: 22px
    .v-ripple__container
      color: $c-primary
    &-subtitle
      color: $c-grey-dark
      font-weight: bold

  .mini-nav__custom-extras .v-list-item-title
    margin-top: 3px

  .mini-nav

    &__label
      +mini-nav-item-hover

    &__trigger
      &:before
        left: 50%
        top: 50%
        transform: translate(-50%,-50%)
        width: 46px
        height: 46px
        border-radius: 50%
      &:before
        background-color: $c-mininav-primary-bg
      span
        color: $c-mininav-color
        i
          height: 26px
          font-size: 34px
          width: 26px
      &:hover
        color: $c-white
        &:before
          opacity: 1

    &__flex
      height: auto
      min-height: 100%

    &__link
      padding-left: 20px

    &__top
      .v-list-item
        &:not(.mini-nav_submenu) .v-list-item__content
          .v-list-item-title
            text-transform: uppercase
            font-size: 0.75rem

    &__business-profile__subtitle
      color: #B3D4F7 !important
      font-weight: normal
      word-break: break-all

  &__avatar
    &:before
      display: none

  .business-nav-bottom
    .v-list-item
      padding: 20px
      min-height: 0
      +maw
        padding: 12px 0 12px 20px
      &.business-nav-bottom-flyout
        padding: 20px
        .v-icon
          +mini-nav-item-hover
        &:hover
          .v-icon
            color: white !important
            caret-color: white !important
        .v-list-item__content
          display: flex
          justify-content: space-between
        + .v-menu
          .v-list-item__content
            display: flex
      &__prepend
        margin: 0
      &__content
        padding: 0
      &__avatar
        margin: 0 12px 0 0
      &.business-profile
        +maw
          padding: 0 15px 0 0
          margin-left: -5px
        .v-icon
          +mini-nav-item-hover
        .business-profile-avatar
          display: flex
          align-items: center
        &:hover
          .v-icon
            color: white !important
            caret-color: white !important
      .v-list-item__content
        display: block // display flex makes list-item-title and list-item-subtitle in same line
        align-items: center
    .v-list-group__header
      padding: 0 20px 0 0
    .v-list-group
      padding: 10px 0 10px 20px
      min-height: 0
    .v-list-group__items .v-list-item
      padding-inline-start: 0!important
    .caret-icon > .v-icon
      width: 16px
      height: 16px

  .business-nav-spacing
    padding-bottom: 18px

  .settings-nav
    .v-divider
      border-color: rgba($c-mininav-color, 0.2)
    &__heading
      display: block
      padding: 20px 20px 0
      margin-bottom: 28px
      font-size: 0.875rem
      font-weight: 800
      letter-spacing: 0.3px
      text-transform: uppercase
      color: rgba($c-mininav-color, 0.5)
    .v-list-item
      padding: 0 0 0 20px
      min-height: 32px
      margin-bottom: 12px
      .v-list-item-title
        text-transform: uppercase
        font-size: 0.75rem
        font-weight: 800
        letter-spacing: 0.3px


.business-nav__overlay-menu

  .v-list-item
    &__content
      font-size: 0.75rem
      color: $c-grey-dark

      &:hover
        color: $c-primary !important

    .v-list-item-title
      font-weight: 700
      font-size: .875rem

.business-nav-menu
  .v-list-item-title
    font-size: 0.875rem
    font-weight: 700
    line-height: 1rem
    padding: 0 25px 0 0
</style>
